import { subMinutes, subHours, subDays } from 'date-fns';
import { DEMO_CAMPAIGNS, DEMO_CLIENTS } from './demoData';

export interface DemoLiveEvent {
  id: string;
  type: 'sent' | 'reply' | 'bounce';
  leadName: string;
  leadEmail: string;
  senderName: string;
  senderEmail: string;
  campaignName: string;
  subject: string;
  timestamp: string;
  status?: string;
}

const generateRandomTime = (minutesAgo: number) => 
  subMinutes(new Date(), minutesAgo).toISOString();

const generateRandomHour = (hoursAgo: number) => 
  subHours(new Date(), hoursAgo).toISOString();

// Generate 4 sent events
const generateSentEvents = (): DemoLiveEvent[] => {
  const events: DemoLiveEvent[] = [];
  
  const campaigns = DEMO_CAMPAIGNS.slice(0, 4);
  const clients = DEMO_CLIENTS.slice(0, 4);
  
  const subjects = [
    'Partnership opportunity with NorthStar Analytics',
    'Revolutionary marketing automation platform',
    'AI-driven insights for your business growth',
    'Strategic partnership opportunity'
  ];
  
  const leadNames = ['Alex Johnson', 'Emma Davis', 'Michael Chen', 'Sarah Wilson'];
  const leadEmails = ['prospect1@techventures.io', 'prospect2@innovatedesign.com', 'prospect3@acmecorp.com', 'prospect4@summittech.io'];
  
  const senderNames = ['sarah.chen', 'marcus.rodriguez', 'emily.thompson', 'david.kim'];
  const senderEmails = ['sarah.chen@northstaranalytics.com', 'marcus.rodriguez@velocitymarketing.com', 'emily.thompson@summittech.io', 'david.kim@healthtechinnovations.com'];
  
  for (let i = 0; i < 4; i++) {
    events.push({
      id: `sent-${i + 1}`,
      type: 'sent',
      leadName: leadNames[i],
      leadEmail: leadEmails[i],
      senderName: senderNames[i],
      senderEmail: senderEmails[i],
      campaignName: campaigns[i].name,
      subject: subjects[i],
      timestamp: generateRandomTime(5 + i * 3), // 5, 8, 11, 14 minutes ago
    });
  }
  
  return events;
};

// Generate 4 reply events
const generateReplyEvents = (): DemoLiveEvent[] => {
  const events: DemoLiveEvent[] = [];
  
  const campaigns = DEMO_CAMPAIGNS.slice(0, 4);
  
  const replySubjects = [
    'Re: Partnership opportunity with NorthStar Analytics',
    'Re: Revolutionary marketing automation platform',
    'Re: AI-driven insights for your business growth',
    'Re: Strategic partnership opportunity'
  ];
  
  const leadNames = ['Alex Johnson', 'Emma Davis', 'Michael Chen', 'Sarah Wilson'];
  const leadEmails = ['prospect1@techventures.io', 'prospect2@innovatedesign.com', 'prospect3@acmecorp.com', 'prospect4@summittech.io'];
  
  const senderNames = ['sarah.chen', 'marcus.rodriguez', 'emily.thompson', 'david.kim'];
  const senderEmails = ['sarah.chen@northstaranalytics.com', 'marcus.rodriguez@velocitymarketing.com', 'emily.thompson@summittech.io', 'david.kim@healthtechinnovations.com'];
  
  for (let i = 0; i < 4; i++) {
    events.push({
      id: `reply-${i + 1}`,
      type: 'reply',
      leadName: leadNames[i],
      leadEmail: leadEmails[i],
      senderName: senderNames[i],
      senderEmail: senderEmails[i],
      campaignName: campaigns[i].name,
      subject: replySubjects[i],
      timestamp: generateRandomTime(2 + i * 2), // 2, 4, 6, 8 minutes ago
      status: 'positive'
    });
  }
  
  return events;
};

// Generate 4 bounce events
const generateBounceEvents = (): DemoLiveEvent[] => {
  const events: DemoLiveEvent[] = [];
  
  const campaigns = DEMO_CAMPAIGNS.slice(0, 4);
  
  const bounceSubjects = [
    'Strategic partnership opportunity',
    'AI-driven insights for your business growth',
    'Revolutionary marketing automation platform',
    'Partnership opportunity with NorthStar Analytics'
  ];
  
  const leadNames = ['Invalid User', 'Unknown Recipient', 'Mailbox Full', 'Domain Not Found'];
  const leadEmails = ['invalid@nonexistent.com', 'unknown@deleteddomain.com', 'full@oldcompany.com', 'bounce@invaliddomain.org'];
  
  const senderNames = ['emily.thompson', 'david.kim', 'sarah.chen', 'marcus.rodriguez'];
  const senderEmails = ['emily.thompson@summittech.io', 'david.kim@healthtechinnovations.com', 'sarah.chen@northstaranalytics.com', 'marcus.rodriguez@velocitymarketing.com'];
  
  for (let i = 0; i < 4; i++) {
    events.push({
      id: `bounce-${i + 1}`,
      type: 'bounce',
      leadName: leadNames[i],
      leadEmail: leadEmails[i],
      senderName: senderNames[i],
      senderEmail: senderEmails[i],
      campaignName: campaigns[i].name,
      subject: bounceSubjects[i],
      timestamp: generateRandomTime(10 + i * 5), // 10, 15, 20, 25 minutes ago
      status: 'hard_bounce'
    });
  }
  
  return events;
};

// Export all demo live events
export const DEMO_LIVE_EMAIL_EVENTS: DemoLiveEvent[] = [
  ...generateSentEvents(),
  ...generateReplyEvents(),
  ...generateBounceEvents()
];

// Export by type for easy filtering
export const DEMO_SENT_EVENTS = DEMO_LIVE_EMAIL_EVENTS.filter(event => event.type === 'sent');
export const DEMO_REPLY_EVENTS = DEMO_LIVE_EMAIL_EVENTS.filter(event => event.type === 'reply');
export const DEMO_BOUNCE_EVENTS = DEMO_LIVE_EMAIL_EVENTS.filter(event => event.type === 'bounce');

// Helper function to get recent events (last 24 hours)
export const getRecentDemoEvents = (type?: 'sent' | 'reply' | 'bounce') => {
  const now = new Date();
  const yesterday = subDays(now, 1);
  
  let events = DEMO_LIVE_EMAIL_EVENTS;
  if (type) {
    events = events.filter(event => event.type === type);
  }
  
  return events.filter(event => new Date(event.timestamp) > yesterday);
};
